import React, { useState, useEffect } from "react";
import { Box, Paper, Avatar } from "@mantine/core";
import {
  IconMessageCircle2,
  IconRobot,
  IconMessageChatbot,
  IconSparkles,
  IconGhost,
  IconBrandHipchat,
} from "@tabler/icons-react";
import ModernMinimalLayout from "../ChatbotWidget/Layouts/ModernMinimalLayout";
import VoiceCentricLayout from "../ChatbotWidget/Layouts/VoiceCentricLayout";
import AssistantStyleLayout from "../ChatbotWidget/Layouts/AssistantStyleLayout";

export default function PreviewWidget({ config }: any) {
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("current");

  useEffect(() => {
    if (config.behavior?.autoPopupEnabled) {
      // Temporarily close the preview, then animate it open to demonstrate the auto-popup
      setIsOpen(false);
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 600); // Wait for close animation to finish before popping open
      return () => clearTimeout(timer);
    } else {
      // If disabled, just keep the preview open normally
      setIsOpen(true);
    }
  }, [config.behavior?.autoPopupEnabled]);

  const dummyMessages = [
    {
      id: 1,
      sender: "bot",
      text: "Hi there! Welcome to our virtual assistant. How can I help you today?",
      options: ["Workout Plan", "Meal Plan", "Gym Exercises"],
    },
    {
      id: 2,
      sender: "user",
      text: "Workout Plan",
    },
    {
      id: 3,
      sender: "bot",
      text: "Sure! What is your fitness goal?",
      options: ["Weight Loss", "Muscle Gain", "Endurance"],
    },
  ];

  const renderLayout = () => {
    const props = {
      messages: dummyMessages,
      inputValue: "",
      setInputValue: () => {},
      handleSend: () => {},
      handleItemSelect: () => {},
      messagesEndRef: null,
      currentStep: "ASK_INTENT",
      config: config,
      activeTab: activeTab,
      setActiveTab: setActiveTab,
    };

    switch (config.uiDesign) {
      case "modern_minimal":
        return <ModernMinimalLayout {...props} />;
      case "voice_centric":
        return <VoiceCentricLayout {...props} />;
      case "assistant_style":
        return <AssistantStyleLayout {...props} />;
      default:
        return <ModernMinimalLayout {...props} />;
    }
  };

  const getPlacementStyles = (isWindow = false) => {
    const placement = config.trigger?.placement || "bottom-right";
    const offset = isWindow ? "80px" : "20px";

    switch (placement) {
      case "bottom-left":
        return { bottom: offset, left: "20px", right: "auto", top: "auto" };
      case "top-right":
        return { top: offset, right: "20px", left: "auto", bottom: "auto" };
      case "top-left":
        return { top: offset, left: "20px", right: "auto", bottom: "auto" };
      case "center-right":
        return {
          top: "50%",
          transform: "translateY(-50%)",
          right: "20px",
          left: "auto",
          bottom: "auto",
        };
      case "center-left":
        return {
          top: "50%",
          transform: "translateY(-50%)",
          left: "20px",
          right: "auto",
          bottom: "auto",
        };
      case "bottom-right":
      default:
        return { bottom: offset, right: "20px", left: "auto", top: "auto" };
    }
  };

  const getTransformOrigin = () => {
    const placement = config.trigger?.placement || "bottom-right";
    switch (placement) {
      case "bottom-left":
        return "bottom left";
      case "top-right":
        return "top right";
      case "top-left":
        return "top left";
      case "center-right":
        return "right center";
      case "center-left":
        return "left center";
      case "bottom-right":
      default:
        return "bottom right";
    }
  };

  const renderTriggerIcon = () => {
    const iconType = config.trigger?.iconType || "chat";
    const iconColor = config.trigger?.iconColor || "#ffffff";
    const triggerSize = config.trigger?.size || 60;
    const iconSize = triggerSize * 0.5;

    switch (iconType) {
      case "robot":
        return <IconRobot size={iconSize} color={iconColor} />;
      case "chatbot":
        return <IconMessageChatbot size={iconSize} color={iconColor} />;
      case "sparkles":
        return <IconSparkles size={iconSize} color={iconColor} />;
      case "ghost":
        return <IconGhost size={iconSize} color={iconColor} />;
      case "hipchat":
        return <IconBrandHipchat size={iconSize} color={iconColor} />;
      default:
        return <IconMessageCircle2 size={iconSize} color={iconColor} />;
    }
  };

  return (
    <Box
      style={{
        width: "100%",
        height: "600px",
        backgroundColor: "#f1f3f5",
        borderRadius: "12px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        style={{
          width: "350px",
          height: "500px",
          position: "absolute",
          ...getPlacementStyles(true),
          boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
          borderRadius: "20px",
          overflow: "hidden",
          backgroundColor: "#fff",
          opacity: isOpen ? 1 : 0,
          transform: isOpen
            ? getPlacementStyles(true).transform || "scale(1)"
            : `${getPlacementStyles(true).transform || ""} scale(0.8)`.trim(),
          transformOrigin: getTransformOrigin(),
          pointerEvents: isOpen ? "auto" : "none",
          transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
          zIndex: 10,
        }}
      >
        {renderLayout()}
      </Box>

      {/* Floating Trigger Icon Preview */}
      <Avatar
        size={config.trigger?.size || 60}
        radius={config.trigger?.radius || "xl"}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: "absolute",
          ...getPlacementStyles(false),
          backgroundColor: config.trigger?.iconBgColor || "#000",
          cursor: "pointer",
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          transition: "transform 0.2s ease",
          transform: isOpen ? "scale(0.9)" : "scale(1)",
          zIndex: 20,
        }}
      >
        {renderTriggerIcon()}
      </Avatar>
    </Box>
  );
}
